/**
 * Geometry for a radar (spider) chart.
 *
 * Axes are spaced evenly around the centre, the first at 12 o'clock and the rest running
 * clockwise. A value's distance from the centre is its fraction of the scale:
 *
 *   shared scale   one range for every axis, (v − min) ÷ (max − min). Zero is included
 *                  automatically unless the reader pins the ends, so the centre of the
 *                  chart is zero and the radius is proportional to the value.
 *   per-axis scale each axis runs from 0 to its own largest value, v ÷ max_j. Axes in
 *                  different units can share a chart, but the shape no longer says which
 *                  axis is larger — only each axis's share of its own maximum.
 *
 * A missing value leaves a gap in the outline rather than being read as zero.
 */

import { niceTicks } from "@/lib/tool-kit/charts/scale";

export type RadarScaling = "shared" | "perAxis";

export interface RadarScale {
  min: number;
  max: number;
  ticks: number[];
  decimals: number;
  /** True when pinned ends were unusable (start not below end) and were ignored. */
  bad: boolean;
}

/** The shared scale over `values`, including zero unless an end is pinned. */
export function sharedScale(
  values: readonly number[],
  low: number | null,
  high: number | null
): RadarScale {
  const finite = values.filter((value) => Number.isFinite(value));
  const dataMin = finite.length > 0 ? Math.min(...finite) : 0;
  const dataMax = finite.length > 0 ? Math.max(...finite) : 1;
  const auto = niceTicks(dataMin, dataMax, { includeZero: true });
  if (low === null && high === null) {
    return { ...auto, bad: false };
  }
  const min = low ?? auto.min;
  const max = high ?? auto.max;
  if (!(min < max)) {
    return { ...auto, bad: true };
  }
  const pinned = niceTicks(min, max, { exact: true });
  return { ...pinned, bad: false };
}

/** Angle of axis `index` of `count`, in radians, 12 o'clock first and clockwise. */
export function axisAngle(index: number, count: number): number {
  return -Math.PI / 2 + (2 * Math.PI * index) / count;
}

export interface RadarPoint {
  x: number;
  y: number;
  value: number;
  /** Distance from the centre as a fraction of the radius, 0–1. */
  fraction: number;
  /** Direction from the centre, for placing a value label just outside the point. */
  angle: number;
}

export interface RadarSeriesShape {
  /** One entry per axis; null where the value is missing. */
  points: (RadarPoint | null)[];
  /** SVG path data for the outline, `Z`-closed only when no value is missing. */
  path: string;
  /** Whether the outline is a closed polygon and so can be filled. */
  closed: boolean;
}

export interface RadarLayoutInput {
  /** `series[s][a]` — the value of series `s` on axis `a`. */
  series: readonly (readonly (number | null)[])[];
  axisCount: number;
  scaling: RadarScaling;
  scale: RadarScale;
  cx: number;
  cy: number;
  radius: number;
}

export interface RadarRing {
  /** The tick value (shared) or percentage (per-axis) the ring stands for. */
  value: number;
  fraction: number;
  /** Corner points, one per axis. */
  corners: { x: number; y: number }[];
}

export interface RadarLayout {
  spokes: { x: number; y: number; angle: number }[];
  rings: RadarRing[];
  shapes: RadarSeriesShape[];
  /** Each axis's own maximum, for per-axis scaling; empty for a shared scale. */
  axisMax: number[];
}

function at(cx: number, cy: number, radius: number, angle: number) {
  return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function outline(points: readonly (RadarPoint | null)[]): {
  path: string;
  closed: boolean;
} {
  const parts: string[] = [];
  let pen = false;
  let missing = false;
  for (const point of points) {
    if (point === null) {
      pen = false;
      missing = true;
    } else {
      parts.push(`${pen ? "L" : "M"}${round1(point.x)},${round1(point.y)}`);
      pen = true;
    }
  }
  const closed = !missing && points.length >= 3;
  return { path: closed ? `${parts.join("")}Z` : parts.join(""), closed };
}

/** Every axis's largest value (at least 0) — the full-scale mark for per-axis scaling. */
export function perAxisMaxima(
  series: readonly (readonly (number | null)[])[],
  axisCount: number
): number[] {
  return Array.from({ length: axisCount }, (_, axis) => {
    let max = 0;
    for (const values of series) {
      const value = values[axis];
      if (value !== null && value !== undefined && value > max) {
        max = value;
      }
    }
    return max;
  });
}

function fractionOf(
  value: number,
  axis: number,
  scaling: RadarScaling,
  scale: RadarScale,
  axisMax: readonly number[]
): number {
  const raw =
    scaling === "perAxis"
      ? value / (axisMax[axis] || 1)
      : (value - scale.min) / (scale.max - scale.min);
  return Math.min(1, Math.max(0, raw));
}

export function layoutRadar(input: RadarLayoutInput): RadarLayout {
  const { axisCount, scaling, scale, cx, cy, radius } = input;
  const axisMax =
    scaling === "perAxis" ? perAxisMaxima(input.series, axisCount) : [];
  const angles = Array.from({ length: axisCount }, (_, i) =>
    axisAngle(i, axisCount)
  );
  const spokes = angles.map((angle) => ({
    ...at(cx, cy, radius, angle),
    angle,
  }));
  const ringSpec =
    scaling === "perAxis"
      ? [20, 40, 60, 80, 100].map((value) => ({ value, fraction: value / 100 }))
      : scale.ticks
          .filter((tick) => tick > scale.min)
          .map((tick) => ({
            value: tick,
            fraction: (tick - scale.min) / (scale.max - scale.min),
          }));
  const rings = ringSpec.map((ring) => ({
    ...ring,
    corners: angles.map((angle) => at(cx, cy, radius * ring.fraction, angle)),
  }));
  const shapes = input.series.map((values) => {
    const points = angles.map((angle, axis): RadarPoint | null => {
      const value = values[axis];
      if (value === null || value === undefined) {
        return null;
      }
      const fraction = fractionOf(value, axis, scaling, scale, axisMax);
      return {
        ...at(cx, cy, radius * fraction, angle),
        value,
        fraction,
        angle,
      };
    });
    return { points, ...outline(points) };
  });
  return { spokes, rings, shapes, axisMax };
}

/** Anchor for the label at the end of a spoke: left of centre ends, right starts. */
export function labelAnchor(angle: number): "start" | "middle" | "end" {
  const cos = Math.cos(angle);
  if (cos > 0.2) {
    return "start";
  }
  return cos < -0.2 ? "end" : "middle";
}

export interface AxisLabelBox {
  lines: string[];
  anchor: "start" | "middle" | "end";
  /** Anchor x, relative to the centre of the chart. */
  x: number;
  /** Baseline of the first line, relative to the centre. */
  y: number;
  /** Vertical extent of the block, relative to the centre. */
  top: number;
  bottom: number;
}

/**
 * Where each axis name goes: just past the end of its spoke, left-aligned on the right,
 * right-aligned on the left, centred above or below. A multi-line name grows away from
 * the chart, so the top label sits above its spoke and the bottom one below.
 * Coordinates are relative to the centre, so the caller can place the centre afterwards
 * once it knows how far the labels reach.
 */
export function layoutAxisLabels(
  lines: readonly (readonly string[])[],
  radius: number,
  {
    fontSize,
    lineHeight,
    gap,
  }: { fontSize: number; lineHeight: number; gap: number }
): AxisLabelBox[] {
  const count = lines.length;
  return lines.map((block, axis) => {
    const angle = axisAngle(axis, count);
    const sin = Math.sin(angle);
    const reach = radius + gap;
    const height = block.length * lineHeight;
    const top = sin * reach + (sin * height) / 2 - height / 2;
    return {
      lines: [...block],
      anchor: labelAnchor(angle),
      x: Math.cos(angle) * reach,
      y: top + fontSize * 0.8,
      top,
      bottom: top + height,
    };
  });
}

/**
 * Pasted rows as radar input: each row is an axis, each value column a series. A series
 * with no number on any axis is dropped. Missing cells stay null.
 */
export function seriesFromRows(
  rows: readonly { label: string; values: readonly (number | null)[] }[],
  seriesCount: number
): (number | null)[][] {
  return Array.from({ length: seriesCount }, (_, series) =>
    rows.map((row) => row.values[series] ?? null)
  );
}

export interface RadarExtreme {
  axis: string;
  name: string;
  value: number;
}

/** The highest and lowest plotted value, with where each sits — for the alt text. */
export function radarExtremes(
  axes: readonly string[],
  series: readonly { name: string; values: readonly (number | null)[] }[]
): { max: RadarExtreme | null; min: RadarExtreme | null } {
  let max: RadarExtreme | null = null;
  let min: RadarExtreme | null = null;
  for (const entry of series) {
    for (const [index, value] of entry.values.entries()) {
      if (value === null) {
        continue;
      }
      const point = { axis: axes[index] ?? "", name: entry.name, value };
      if (!max || value > max.value) {
        max = point;
      }
      if (!min || value < min.value) {
        min = point;
      }
    }
  }
  return { max, min };
}
